import clsx from "clsx";
import { type FC, type ReactNode, useEffect, useState } from "react";
import { useBoolean, useCopyToClipboard } from "usehooks-ts";

import { AddUserModal } from "../AddUserModal";

import { AdminUserOptionsMenu } from "./AdminUserOptionsMenu";
import { OrgRoleIconWithTooltip } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { EditOrgUserModal } from "components/modals/EditOrgUserModal";
import type { OrgRoleType, OrgUserType, UserType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { LegacyButton } from "mds/components/LegacyButton";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCourses,
  selectCurrentUserId,
  selectCurrentUserIsMPAdmin,
  selectEnabledCourseUsersByRole,
  selectIsEditModeEnabled,
  selectOrgUsers,
  selectUsers,
} from "store/selectors";
import { selectUserInviteStatuses } from "store/selectors/computed";
import { sortByLastName } from "store/selectors/sorting";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { getCourseResourceRole } from "utils/section";
import { useFetchUserInviteStatuses } from "utils/user-invite-status";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

type AdminUserEntryType = OrgUserType &
  UserType & {
    org_user_id: string;
    tooltipCourseRoleTable: ReactNode;
    invite_status: InviteStatus;
  };

interface AdminUsersListProps {
  onShowBulkImportModal: () => void;
}

export const AdminUsersList: FC<AdminUsersListProps> = ({ onShowBulkImportModal }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const allUsers = useListSelector(selectUsers);
  const orgUsers = useAppSelector(selectOrgUsers);
  const userInviteStatuses = useAppSelector(selectUserInviteStatuses);
  const courseUsers = useListSelector(selectEnabledCourseUsersByRole);
  const courses = useListSelector(selectCourses);
  const { canAuthorOrg, canSeeAllOrgUsers } = useCapabilities();
  const editModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isMpAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const user_id = useAppSelector(selectCurrentUserId);
  const canToggleDisabled = canAuthorOrg;
  const canChangeOrgRole = canAuthorOrg;
  const canImpersonate = isMpAdmin;
  const isCurrentUserInOrg = orgUsers.some((ou) => ou.user_id === user_id);
  const canAddThemselvesToOrg = isMpAdmin && !isCurrentUserInOrg;
  const isSmallScreen = !useIsMdOrLarger();

  const [, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    storeApi.course_users.list({});
  }, []);

  useFetchUserInviteStatuses();

  // OrgUsers and CourseUsers are both links to the User model, and to Org and Course separately,
  // and both have a "role" string attached.
  // In this view we'd like to show a list of all users that have an OrgUser, their role, and the
  // amount of CourseUsers that they have.

  const userList: AdminUserEntryType[] = orgUsers
    .flatMap((orgUser) => {
      const user = allUsers.find((u) => u.id === orgUser.user_id);
      if (!user || orgUser.role === "recording_agent") {
        return [];
      }
      if (!canSeeAllOrgUsers && user.is_superuser) {
        return [];
      }

      const courseUsersForUser = courseUsers.filter((cu) => cu.user_id === orgUser.user_id);

      const tooltipCourseRoleTable = courseUsersForUser.map((cu) => {
        const course = courses.find((c) => c.id === cu.section_id);
        const role = getCourseResourceRole(cu.role);

        return (
          <div key={cu.id}>
            <strong>{course?.title}</strong>: {t(`roles.course_${role}`)}
          </div>
        );
      });

      return [
        {
          ...orgUser,
          ...user,
          org_user_id: orgUser.id,
          tooltipCourseRoleTable,
          invite_status: userInviteStatuses[user.id] || InviteStatus.NOT_SENT,
        },
      ];
    })
    .toSorted(sortByLastName);

  // We filter out the current user, so we can list it first
  const currentUser = userList.find((orgUser) => orgUser.user_id === user_id);
  const filteredUserList = userList.filter((orgUser) => orgUser.user_id !== user_id);
  const canSeeExternalUserIds = canAuthorOrg && userList.some((u) => u.external_user_id);

  const onJoinAsAdmin = () => {
    storeApi.org_users
      .create({
        user_id,
        role: "admin",
      })
      .catch(toastLocalizedOperationCatcher("join_as_admin"));
  };

  const onChangeRole = (newRole: "admin" | "other", user: { org_user_id: string }) => {
    storeApi.org_users
      .partial_update({
        id: user.org_user_id,
        role: newRole,
      })
      .catch(toastLocalizedOperationCatcher("update_user_role"));
  };

  const onImpersonate = (user: { id: string }) => {
    createImpersonationUrl(user.id).then(copyToClipboard);
  };

  const onToggleDisabled = (user: { org_user_id: string; is_disabled: boolean }) => {
    storeApi.org_users.partial_update({
      id: user.org_user_id,
      is_disabled: !user.is_disabled,
    });
  };

  return (
    <div className="h-full overflow-y-auto pb-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="mb-0 hidden md:block">{t("user_options_menu.org_sidebar_title")}</h3>
        <div className={clsx("flex gap-2", isSmallScreen && "w-full")}>
          {canAddThemselvesToOrg && (
            <LegacyButton className="flex items-center gap-2" size="xs" onClick={onJoinAsAdmin}>
              {/* Do NOT localize this as it's mp admin only */}
              <span>Join this org as an admin</span>
              <Tooltip text="After joining, you can choose your role in the list below." />
            </LegacyButton>
          )}

          <Button
            aria-label={t("admin.add_user.add_user")}
            className="flex-1 md:flex-none"
            disabled={!editModeEnabled}
            eventName="Org Users - Add new"
            kind="white"
            size="xs"
            onClick={() => setShowAddUserModal(true)}
          >
            {t("admin.add_user.add_user")}
          </Button>

          <Button
            aria-label={t("home.settings.bulk_import_modal.title")}
            className="flex-1 md:flex-none"
            disabled={!editModeEnabled}
            eventName="Org Users - Bulk enrollment"
            kind="white"
            size="xs"
            onClick={onShowBulkImportModal}
          >
            {t("home.settings.bulk_import_modal.title")}
          </Button>
        </div>
      </div>

      <ScrollableTableContainer className="mb-0">
        <Table>
          <THead>
            <tr>
              <TH>{t("user_options_menu.table_header_name")}</TH>
              <TH>{t("user_options_menu.table_header_email")}</TH>
              {canSeeExternalUserIds && <TH>{t("user_options_menu.table_header_external_id")}</TH>}
              <TH>{t("user_options_menu.table_header_invite_status")}</TH>
            </tr>
          </THead>

          <tbody>
            {currentUser && (
              <AdminUserEntry
                showExternalId={canSeeExternalUserIds}
                user={currentUser}
                onChangeRole={canChangeOrgRole ? onChangeRole : undefined}
                onImpersonate={canImpersonate ? onImpersonate : undefined}
                onToggleDisabled={canToggleDisabled ? onToggleDisabled : undefined}
              />
            )}

            {filteredUserList.map((user) => (
              <AdminUserEntry
                key={user.id}
                showExternalId={canSeeExternalUserIds}
                user={user}
                onChangeRole={canChangeOrgRole ? onChangeRole : undefined}
                onImpersonate={canImpersonate ? onImpersonate : undefined}
                onToggleDisabled={canToggleDisabled ? onToggleDisabled : undefined}
              />
            ))}
          </tbody>
        </Table>
      </ScrollableTableContainer>
      {showAddUserModal && <AddUserModal onClose={() => setShowAddUserModal(false)} />}
    </div>
  );
};

type AdminUserEntryProps = {
  user: AdminUserEntryType;
  onChangeRole?: (newRole: OrgRoleType, user: { org_user_id: string }) => void;
  onImpersonate?: (user: { id: string }) => void;
  onToggleDisabled?: (user: { org_user_id: string; is_disabled: boolean }) => void;
  showExternalId?: boolean;
};

export const AdminUserEntry: FC<AdminUserEntryProps> = ({
  user,
  onChangeRole,
  onImpersonate,
  onToggleDisabled,
  showExternalId = false,
}) => {
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean(false);
  const hasActions = Boolean(onChangeRole || onImpersonate || onToggleDisabled);
  return (
    <TR className="items-center justify-between">
      <TD
        className={clsx("flex items-center gap-1", user.is_disabled && "italic text-black-tint-70")}
      >
        <OrgRoleIconWithTooltip
          extraContent={user.tooltipCourseRoleTable}
          role={user.is_superuser ? "superuser" : user.role}
        />

        <span className="max-w-m mb-1 truncate" title={getUserFullName(user)}>
          {getUserFullName(user)}
        </span>
      </TD>

      <TD className="body-xs relative">{user.email}</TD>

      {showExternalId && <TD className="body-xs relative">{user.external_user_id || "-"}</TD>}

      <TD className="body-xs relative">
        <span>{t(`invite_status.${user.invite_status}`)}</span>
        {hasActions && (
          <div className="absolute right-0 top-0">
            <AdminUserOptionsMenu
              currentRole={user.is_superuser ? "superuser" : user.role}
              user={user}
              onChangeRole={onChangeRole ? (newRole) => onChangeRole(newRole, user) : undefined}
              onEdit={openModal}
              onImpersonate={onImpersonate ? () => onImpersonate(user) : undefined}
              onToggleDisabled={onToggleDisabled ? () => onToggleDisabled(user) : undefined}
            />
          </div>
        )}

        <EditOrgUserModal open={isModalOpen} user={user} onClose={closeModal} />
      </TD>
    </TR>
  );
};
